import { AppState, AuthProvider } from '@statsbomb/kitbag-auth';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import LogRocket from 'logrocket';
import { lazy, ReactNode, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import env from '@/env';
import { useErrorMonitoring } from '@/hooks/useErrorMonitoring';
import { getQueryClient } from '@/query/client';
import { AppProviders } from './AppProviders';
import { I18nProvider } from './I18nProvider';

// import.meta.env.DEV is statically replaced by Vite, so esbuild can prove this branch (and the
// dynamic import it guards) is unreachable in a built bundle and drop it entirely — jotai-devtools'
// stylesheet alone is ~715KB gzip, and it has no business shipping to staging/production.
const DevToolsPanel = import.meta.env.ENABLE_DEV_TOOLS ? lazy(() => import('./DevToolsPanel')) : null;

// VITE_TEST_MODE=true only during dev:msw — Vite replaces it statically, tree-shaking this import from prod.
const MockAuthProvider =
  import.meta.env.VITE_TEST_MODE === 'true'
    ? lazy(() => import('./MockAuthProvider').then(providerModule => ({ default: providerModule.MockAuthProvider })))
    : null;

const LogRocketProvider = ({ children }: { children: ReactNode }) => {
  LogRocket.init('erd/sb-iq', { rootHostname: env.VITE_LOGROCKET_ROOT_HOSTNAME });
  return children;
};

const SessionReplayProvider = ({ children }: { children: ReactNode }) =>
  Number(env.VITE_ENABLE_LOGROCKET) ? <LogRocketProvider>{children}</LogRocketProvider> : children;

const queryClient = getQueryClient();

const AuthedClientProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const onRedirectCallback = (appState?: AppState) => {
    LogRocket.startNewSession();
    navigate((appState && appState.returnTo) || window.location.pathname);
  };

  return (
    <AuthProvider
      providerOptions={{
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access',
        },
        clientId: env.VITE_AUTH0_CLIENT_ID,
        domain: env.VITE_AUTH0_DOMAIN,
        onRedirectCallback,
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
        cacheLocation: 'memory',
      }}
    >
      <AppProviders>{children}</AppProviders>
    </AuthProvider>
  );
};

// AuthedClientProvider must be declared before this line to avoid a temporal dead zone ReferenceError.
const ClientProvider = MockAuthProvider ?? AuthedClientProvider;

export const Providers = ({ children }: { children: ReactNode }) => {
  useErrorMonitoring();

  const clientTree = (
    <ClientProvider>
      {!MockAuthProvider && DevToolsPanel && (
        <Suspense fallback={null}>
          <DevToolsPanel />
        </Suspense>
      )}
      {children}
    </ClientProvider>
  );

  return (
    <I18nProvider>
      <SessionReplayProvider>
        <QueryClientProvider client={queryClient}>
          {/* Only the MSW tree needs a Suspense boundary (ClientProvider is lazy there); production keeps
              the tree it had before Layer 4. The fallback must be null: the ThemeProvider lives inside
              AppProviders, so a themed fallback would call useTheme outside a ThemeProvider. */}
          {MockAuthProvider ? <Suspense fallback={null}>{clientTree}</Suspense> : clientTree}
          {env.VITE_ENABLE_TANSTACK_DEVTOOLS === '1' && <ReactQueryDevtools position="bottom" />}
        </QueryClientProvider>
      </SessionReplayProvider>
    </I18nProvider>
  );
};
